import { formatDateToApiUTC, hasItems } from "@repo/utils";
import useSWR, { mutate } from "swr";

import * as fetcher from "../fetcher";
import { mutateWithStartKey } from "../mutateWithStartKey";
import { type Task } from "./types";

interface GetTaskProps {
  emails?: string[];
  fromDate?: Date;
  toDate?: Date;
  opportunityId?: string;
  teamId?: number;
  assignedToTeam?: boolean;
  subTaskType?: string;
  unitId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  parentTaskType?: string;
}

export const stringifyGetTasksQuery = (props?: GetTaskProps) => {
  if (!props) {
    return "";
  }
  const searchParams = new URLSearchParams();

  if (hasItems(props.emails)) {
    searchParams.set("emailId", props.emails.join(","));
  }
  if (props.fromDate) {
    searchParams.set("fromDate", formatDateToApiUTC(props.fromDate));
  }
  if (props.toDate) {
    searchParams.set("toDate", formatDateToApiUTC(props.toDate));
  }
  if (props.opportunityId) {
    searchParams.set("opportunityId", props.opportunityId);
  }
  if (props.teamId) {
    searchParams.set("teams", String(props.teamId));
  }
  if (props.assignedToTeam) {
    searchParams.set("assignedToTeams", String(props.assignedToTeam));
  }
  if (props.parentTaskType) {
    searchParams.set("parentTaskType", props.parentTaskType);
  }
  if (props.subTaskType) {
    searchParams.set("subTaskType", props.subTaskType);
  }
  if (props.unitId) {
    searchParams.set("unitId", String(props.unitId));
  }
  if (props.dueDateFrom) {
    searchParams.set("dueDateFrom", props.dueDateFrom);
  }
  if (props.dueDateTo) {
    searchParams.set("dueDateTo", props.dueDateTo);
  }

  return searchParams.toString();
};

export const useTask = (id?: number) => {
  return useSWR<Task>(id ? `/opportunities/tasks/${id}` : null, (url: string) =>
    fetcher.core.get<Task>(url)
  );
};

export const useTasks = (props?: GetTaskProps) => {
  let key = null;

  // Require OpportunityID or email or teamID to run query
  if (props !== undefined) {
    if (props.opportunityId !== undefined) {
      key = `/opportunities/tasks?opportunityId=${props.opportunityId}`;
    } else if (props.emails !== undefined && props.emails.length > 0) {
      key = `/opportunities/tasks?${stringifyGetTasksQuery(props)}`;
    } else if (props.teamId !== undefined) {
      key = `/opportunities/tasks?${stringifyGetTasksQuery(props)}`;
    }
  }

  return useSWR<Task[]>(
    key,
    (url) => {
      return fetcher.core.get<Task[]>(url);
    },
    {
      keepPreviousData: true,
      revalidateOnFocus: true,
      refreshInterval: 60 * 1000,
    }
  );
};

export const createAndUpdateTask = (task: Partial<Task>): Promise<Task> => {
  return fetcher.core.update("/opportunities/tasks", task).then(async (res) => {
    mutateWithStartKey("/opportunities/tasks");
    mutate(`/opportunities/${task.opportunityId}/history`);
    return await res.json();
  });
};

export const toggleDone = (
  task: Partial<Task>,
  isDone: boolean,
  opportunityId?: number
) => {
  return fetcher.core
    .update("/opportunities/tasks", { ...task, isDone })
    .then((res) => {
      mutateWithStartKey("/opportunities/tasks");
      mutate(`/opportunities/${opportunityId}/history`);
      return res;
    });
};

export const deleteTask = (taskId: number) => {
  return fetcher.core.remove(`/opportunities/tasks/${taskId}`).then((res) => {
    mutateWithStartKey("/opportunities/tasks");
    return res;
  });
};
